import {
	Button as HeroButton,
	type ButtonProps as HeroButtonProps,
} from "@heroui/react"
import type { ReactNode } from "react"

/**
 * 프로젝트 전용 Button variant
 */
export type ButtonVariant =
	| "primary"
	| "secondary"
	| "outline"
	| "ghost"
	| "destructive"

/**
 * HeroUI Button을 확장한 프로젝트 공통 Button Props
 */
export interface ButtonProps
	extends Omit<HeroButtonProps, "color" | "variant"> {
	/**
	 * 버튼 스타일 variant
	 * @default "primary"
	 */
	variant?: ButtonVariant

	/**
	 * 로딩 상태 (HeroUI isLoading 활용)
	 */
	isLoading?: boolean

	/**
	 * 왼쪽 아이콘 (HeroUI startContent로 전달)
	 */
	leftIcon?: ReactNode

	/**
	 * 오른쪽 아이콘 (HeroUI endContent로 전달)
	 */
	rightIcon?: ReactNode
}

export function Button({
	variant = "primary",
	isLoading,
	leftIcon,
	rightIcon,
	children,
	...props
}: ButtonProps) {
	// variant에 따른 HeroUI color/variant 매핑
	const colorMap: Record<
		ButtonVariant,
		HeroButtonProps["color"]
	> = {
		primary: "primary",
		secondary: "secondary",
		outline: "default",
		ghost: "default",
		destructive: "danger",
	}

	const variantMap: Record<
		ButtonVariant,
		HeroButtonProps["variant"]
	> = {
		primary: "solid",
		secondary: "solid",
		outline: "bordered",
		ghost: "light",
		destructive: "solid",
	}

	return (
		<HeroButton
			color={colorMap[variant]}
			variant={variantMap[variant]}
			isLoading={isLoading}
			startContent={leftIcon}
			endContent={rightIcon}
			className={`cursor-pointer disabled:cursor-not-allowed ${props.className || ""}`}
			{...props}
		>
			{children}
		</HeroButton>
	)
}
